import React, { useState, useEffect } from "react";
import { Link, redirect, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../context/axiosConfig";
import requireAuth from "../context/requireAuth";
import { toDeletePage } from '../lib/toDeletePage';
import {
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
} from 'mdb-react-ui-kit';
import { MaintenanceSelector } from "../components/MaintenanceSelector";
import { FaTimes } from "react-icons/fa";

// Locks the <body> scroll while a modal is open (mobile/tablet friendly, incl. iOS)
function useBodyScrollLock(locked) {
  useEffect(() => {
    if (!locked) return;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [locked]);
}

function PlantModel({ selectingModeActive, plant, rowIndex, colIndex, handlePlantDivClick, handlePlantSelectClick, alarmLevel }) {
  const [selected, setSelected] = useState(false);

  const handlePlantClick = (event) => {
    if (selectingModeActive) {
      setSelected(!selected);
      handlePlantSelectClick(!selected, plant)
    } else {
      handlePlantDivClick(event)
    }
  }

  useEffect(() => {
    setSelected(false);
  }, [selectingModeActive])

  // Determine alarm level CSS class
  const getAlarmClass = () => {
    if (!alarmLevel) return '';
    switch (alarmLevel.toLowerCase()) {
      case 'high': return 'GridAlarmHigh';
      case 'medium': return 'GridAlarmMedium';
      case 'low': return 'GridAlarmLow';
      default: return '';
    }
  };

  return (
    <div
      key={`location_[${colIndex},${rowIndex}]`}
      className={`GridItem ${selectingModeActive ? "GridSelectingColor" : ""} ${selected ? "GridSelectedColor" : ""} ${getAlarmClass()}`}
      data-col={colIndex + 1}
      data-row={rowIndex + 1}
      data-plantno={plant.org_plant_no}
      data-plantid={plant.plant_id}
      onClick={handlePlantClick}
    >
      {plant.org_plant_no} - {plant.variety}
    </div>
  )

}

function Plot() {
  const [plants, setPlants] = useState([]);
  const [plot, setPlot] = useState({});
  const [loading, setLoading] = useState(false);
  const [plantInPlot, setPlantInPlot] = useState([]);
  const [plantsNotInPlot, setplantsNotInPlot] = useState([]);
  const [NoPlantShowModal, setNoPlantShowModal] = useState(false);
  const [PlantShowModal, setPlantShowModal] = useState(false);
  const [plotMaintenanceModal, setPlotMaintenanceModal] = useState(false);
  const [selectedPlantsMaintenanceModal, setSelectedPlantsMaintenanceModal] = useState(false);
  const [selectedCol, setSelectedCol] = useState("");
  const [selectedRow, setSelectedRow] = useState("");
  const [selectedUid, setSelectedUid] = useState("");
  const [inputs, setInputs] = useState({ org_plant_no: "" });
  const [grid, setGrid] = useState([]);
  const { id } = useParams();
  const [selectingModeActive, setSelectingModeActive] = useState(false);
  const [selectedUids, setSelectedUids] = useState(new Set());
  const [gridHtml, setGridHTML] = useState([]);
  const [selected_plant_id, setSelected_plant_id] = useState(null);
  const [selected_plant_ids, setSelected_plant_ids] = useState(new Set());
  const [allPlantIds, setAllPlantIds] = useState([]);
  const [plotAlarmData, setPlotAlarmData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false)

  // Lock body scroll whenever any modal is open
  useBodyScrollLock(
    NoPlantShowModal || PlantShowModal || plotMaintenanceModal || selectedPlantsMaintenanceModal
  );

  const org_id = localStorage.getItem('organization');

  useEffect(() => {
          axios.get(`${org_id}/checkuser`, {withCredentials: true})
              .then(res => {
              if (res.data.role === "admin") {
                  setIsAdmin(true);
              }
              })
              .catch(err => console.log(err));
      }, [])

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        axios.get(`orgs/${org_id}/plants/`, { withCredentials: true }).then((res) => {setPlants(res.data); console.log(res.data)}),
        axios.get(`orgs/${org_id}/plots/${id}`, { withCredentials: true }).then((res) => {setPlot(res.data); console.log(res.data)}),
        // Fetch alarm levels for the plot
        axios.get(`orgs/${org_id}/plots/${id}/alarm-levels`, { withCredentials: true }).then((res) => {
          setPlotAlarmData(res.data);
          console.log('Plot alarm data:', res.data);
        }).catch((err) => {
          console.log('No alarm data available or error:', err);
          setPlotAlarmData(null);
        })
      ]);
      setLoading(false);
      // console.log(plants)
    } catch (err) {
      console.log(err);
    }
  };

  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    console.log('new plant fetch')
    fetchData();
  }, [id, NoPlantShowModal, PlantShowModal]);

  useEffect(() => {
    setPlantInPlot(plants.filter((plant) => plant.plot_id == id));
    setplantsNotInPlot(plants.filter((plant) => plant.plot_id == null && plant.datedestroyed == null));
  }, [plants])

  useEffect(() => {
    if (plot?.size_x && plot?.size_y) {
      console.log('reload grid');
      fillGrid(plantInPlot);
    }
  }, [plantInPlot, plot]);

  useEffect(() => {
    setSelectedUids(new Set());
    setSelected_plant_ids(new Set());
    setGridHTML(renderGridHtml())
  }, [grid, selectingModeActive, plotAlarmData])

  useEffect(() => {
    if (plantInPlot && plantInPlot.length > 0) {
      // Extract all plant IDs from plants in this plot
      const plantIds = plantInPlot.map(plant => plant.plant_id);
      setAllPlantIds(plantIds);
    }
  }, [plantInPlot]);

  const handleSelectPlantsClick = (event) => {
    setSelectingModeActive(!selectingModeActive);
  }

  const handleNoPlantDivClick = (event) => {
    const col = event.target.getAttribute("data-col");
    const row = event.target.getAttribute("data-row");
    setSelectedCol(col);
    setSelectedRow(row);
    setNoPlantShowModal(true);
  };

  const handlePlantDivClick = (event) => {

    const col = event.target.getAttribute("data-col");
    const row = event.target.getAttribute("data-row");
    const uid = event.target.getAttribute("data-plantno");
    const raw_plant_id = event.target.getAttribute("data-plantid");
    setSelectedCol(col);
    setSelectedRow(row);
    setSelectedUid(uid);
    setSelected_plant_id(raw_plant_id);
    setPlantShowModal(true);
  };

  const handlePlantSelectClick = (selected, plant) => {
    if (selected) {
      selectedUids.add(plant.org_plant_no)
      selected_plant_ids.add(plant.plant_id)
      setSelectedUids(selectedUids)
      setSelected_plant_ids(selected_plant_ids)

    } else {
      selectedUids.delete(plant.org_plant_no)
      selected_plant_ids.delete(plant.plant_id)
      setSelectedUids(selectedUids)
      setSelected_plant_ids(selected_plant_ids)
    }
  }

  const hanldeBulkDestroyClick = (event) => {
    const deleteObject = {
      type: "plant",
      page: location.pathname,
      objects: Array.from(selected_plant_ids),
      ids: Array.from(selectedUids)
    }
    setSelectedUids(new Set())
    setSelected_plant_ids(new Set())
    window.sessionStorage.setItem("toDelete", JSON.stringify(deleteObject));
    navigate("/destroy")
  }

  const handleRemoveBulkClick = (event) => {
    event.preventDefault(); // <-- stops the page from reloading

    const body = {
      ids: Array.from(selected_plant_ids)
    }
    
    axios.delete(`orgs/${org_id}/plants`, { 
        withCredentials: true,
        data: body
      })
    .then((res) => {
      setSelectedUids(new Set());
      setSelected_plant_ids(new Set());
      alert("Plants were removed");
      fetchData();
    })
    .catch((error) => {
      console.error(error);
      alert("Something went wrong! Try Again");
    });
  };

  const handleSelectedPlantsMaintenanceClick = () => {
    if (selected_plant_ids.size > 0) {
      setSelected_plant_id(Array.from(selected_plant_ids));
      const selectedPlantNumbers = Array.from(selectedUids);
      setSelectedUid(`Selected Plants: ${selectedPlantNumbers.join(', ')}`);
      setSelectedPlantsMaintenanceModal(true);
    }
  }

  const toggleSelectedPlantsMaintenanceModal = () => {
    setSelectedPlantsMaintenanceModal(!selectedPlantsMaintenanceModal);
  }

  function fillGrid(plantsInPlot) {
  const nextGrid = Array.from({ length: plot.size_y }, () =>
    Array.from({ length: plot.size_x }, () => null)
  );

  plantsInPlot.forEach((plant) => {
    const row = Number(plant.loc_y);      // no -1
    const col = Number(plant.loc_x);      // no -1

    if (
      row >= 0 && row < plot.size_y &&
      col >= 0 && col < plot.size_x
    ) {
      nextGrid[row][col] = plant;         // row → Y, col → X
    } else {
      console.warn(
        `Plant ${plant.org_plant_no} has coordinates (${col}, ${row}) outside the plot`
      );
    }
  });

  setGrid(nextGrid);

}

// Helper function to get alarm level for a specific plant
const getPlantAlarmLevel = (plantId) => {
  if (!plotAlarmData?.plant_alarm_levels) return null;
  const plantAlarm = plotAlarmData.plant_alarm_levels.find(
    plant => plant.plant_id === plantId
  );
  return plantAlarm?.alarm_level || null;
};

function renderGridHtml() {
  return grid.map((row, rowIndex) => (
    <div key={rowIndex} className="GridRow">
      {row.map((plant, colIndex) => {
        if (plant) {
          const alarmLevel = getPlantAlarmLevel(plant.plant_id);
          return (
            <PlantModel
              key={`plant_${plant.plant_id}_${colIndex}_${rowIndex}`}
              rowIndex={rowIndex}
              colIndex={colIndex}
              plant={plant}
              selectingModeActive={selectingModeActive}
              handlePlantDivClick={handlePlantDivClick}
              handlePlantSelectClick={handlePlantSelectClick}
              alarmLevel={alarmLevel}
            />
          );
        } else {
          return (
            <div
              key={`location_[${colIndex},${rowIndex}]`}
              data-col={colIndex + 1}
              data-row={rowIndex + 1}
              className="GridItem nonActive"
              onClick={handleNoPlantDivClick}
            >
              No Plant
            </div>
          );
        }
      })}
    </div>
  ));
}

  console.log("Grid HTML", gridHtml);

  const togglePlotMaintenanceModal = () => {
    setPlotMaintenanceModal(!plotMaintenanceModal);
  }
  
  const handlePlotMaintenanceClick = () => {
    // Set the selected_plant_id to the array of all plant IDs in the plot
    setSelected_plant_id(allPlantIds);
    setSelectedUid(`Plot: ${plot.plotno}`);
    setPlotMaintenanceModal(true);
  }

  const handleChange = (e) => {
    setInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleRemoveSubmit = (e) => {
    e.preventDefault();
    const body = {
      ids: [selected_plant_id],
    }
    axios
      .delete(`orgs/${org_id}/plants`, { 
        withCredentials: true,
        data: body
      })
      .then((res) => {
        alert(`Plant ${selectedUid} was removed`);
        setPlantShowModal(false);
        fetchData();
      })
      .catch((error) => {
        console.error(error);
        alert("Something went wrong! Try Again");
      });
  };

  const handleDestroySubmit = (e) => {
    e.preventDefault();
    const deleteObject = {
      type: "plant",
      page: location.pathname,
      objects: [selected_plant_id],
      ids: [selectedUid]
    }
    toDeletePage(navigate, deleteObject);

  };

  const handlePlaceSubmit = async (e) => {
  e.preventDefault();

  try {
    const res = await axios.get(`orgs/${org_id}/plants/`, { withCredentials: true });
    const plant = res.data.find(p => String(p.org_plant_no) === String(inputs.org_plant_no));

    if (!plant || plant.datedestroyed) {
      alert(`Cannot update Plant with org_plant_no ${inputs.org_plant_no}: either it doesn't exist or is destroyed.`);
      return;
    }

    const plantValues = {
      plot_id: id,
      loc_y: selectedRow - 1,
      loc_x: selectedCol - 1,
    };

    plantValues.loc_y = String(plantValues.loc_y);
    plantValues.loc_x = String(plantValues.loc_x);

    console.log("PATCHing", plant.plant_id, plantValues);

    await axios.patch(`orgs/${org_id}/plants/${plant.plant_id}`, plantValues, { withCredentials: true });

    setNoPlantShowModal(false);
    fetchData();

  } catch (err) {
    console.error("Place Submit Failed:", err);
    alert("Something went wrong! Check console for details.");
  }
};
  const plantIds = plantsNotInPlot.map((item) => item.org_plant_no).join(', ');

  const repeatAction = (repeat) => {
    if (repeat) {
      setSelectedCol(null);
      setSelectedRow(null);
    } else {
      setPlantShowModal(false);
    }
  }
  const toggleShow = () => {
    setPlantShowModal(!PlantShowModal);
  }

  const toggleNoPlantShow = () => {
    setNoPlantShowModal(!NoPlantShowModal);
  }

  const deletePlot = (e) => {
    e.preventDefault();
    // axios
    //   .delete(`/plot/${plot.plotid}`)
    //   .then((res) => {
    //     alert(res.data.message);
    //     setPlantShowModal(false);
    //     fetchData();
    //     goBack()
    //   })
    //   .catch((error) => {
    //     console.error(error);
    //     alert(`Something went wrong! ${error.request.response}`);
    //   });

    e.preventDefault();

    const deleteObject = {
      type: "plot",
      page: "/main",
      objects: [plot.plot_id],
      ids: [plot.org_plot_no]
    }
    toDeletePage(navigate, deleteObject);
  }


  return (
    <div className="bodyContent">
      <h2 className="subheader">Plot {plot.plotno}</h2>
      <br />
      <Link to={`/editPlot/${plot.plot_id}`}>
        <input type="button" value="Edit Plot" className="purpleBtn" />
      </Link>
      <input type="button" value={`${selectingModeActive ? "Deselect" : "Select"} Plants`} className="purpleBtn" onClick={handleSelectPlantsClick} />
      <Link to={`/newPlant`}>
        <input type="button" value="New Plant" className="purpleBtn" />
      </Link>
      {isAdmin && (
      <form method="POST" onSubmit={deletePlot} className="inlineDeleteBtn">
        <input
          type="submit"
          value="Delete Plot"
          className="deleteBtn roomDeleteBtn"
        />
      </form>
      )}
      
        <div className={`PlantSelectActions ${!selectingModeActive ? "PlantSelectActionsHidden" : ""}`}>
        <button className="purpleBtn" onClick={handleRemoveBulkClick}>
          Remove Plants
        </button>
        {isAdmin && (
        <button className="purpleBtn" onClick={hanldeBulkDestroyClick}>
          Destroy Plants
        </button>
        )}
        <button className="purpleBtn" onClick={handleSelectedPlantsMaintenanceClick}>
          Maintenance for Selected Plants
        </button>
      </div>
      
      {/* <Link to="/replacePlot">
        <input
          type="button"
          value="Replace Plot"
          className="purpleBtn marginTop10"
        />
      </Link> */}

      {/* Make grid dynamic with database values */}

      <div className="Grid" style={{ width: "fit-content" }}>{gridHtml}</div>

      {NoPlantShowModal && (
        <MDBModal show={NoPlantShowModal} setShow={setNoPlantShowModal} tabIndex="-1">

          <MDBModalDialog scrollable>
            <MDBModalContent
              style={{ maxHeight: "calc(100vh - 4rem)", overflowY: "auto", WebkitOverflowScrolling: "touch" }}
            >
              <MDBModalHeader>
                <MDBModalTitle>

                  <div className="">
                    <button
                      className="close-btn"
                      onClick={toggleNoPlantShow}
                    >
                      <FaTimes />
                    </button>

                    <h1 className="title">Place Plant</h1>
                    <div className="centered relativeCentered">
                      <p>Row: {selectedRow} Column: {selectedCol}</p>
                    </div>
                    <label htmlFor="org_plant_no">Plant ID</label>

                    <div className="editFormDiv">
                      <form className="editForm" onSubmit={handlePlaceSubmit}>
                        <input
                          type="number"
                          name="org_plant_no"
                          id="org_plant_no"
                          className="editInput newMaintenanceInput"
                          onChange={handleChange}
                        />
                        <br />
                        <input
                          type="submit"
                          value="Place Plant"
                          className="maintenanceFormSubmit"
                        />

                      </form>
                    </div>
                  </div>
                </MDBModalTitle>
              </MDBModalHeader>
              <MDBModalBody>


                <h1>Plants that are not in a plot: </h1>
                <ul style={{ width: "100%", margin: "0px 20px", padding: "0", transform: "translateX(-20px)" }}>
                  {plantIds}
                </ul>
              </MDBModalBody>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      )}

      {PlantShowModal && (
        <MDBModal show={PlantShowModal} setShow={setPlantShowModal} tabIndex="-1">
          <MDBModalDialog scrollable>
            <MDBModalContent
              style={{ maxHeight: "calc(100vh - 4rem)", overflowY: "auto", WebkitOverflowScrolling: "touch" }}
            >
              <MDBModalHeader>
                <MDBModalTitle>
                  <div>
                    <button
                      className="close-btn"
                      onClick={toggleShow}
                    >
                      <FaTimes />
                    </button>
                    <h1 className="title">Info Plant: {selectedUid}</h1>
                    <p>Row: {selectedRow} Column: {selectedCol}</p>
                  </div>
                  <div className="editFormDiv leftAlign">
                    <form className="editForm smallForm" onSubmit={handleRemoveSubmit}>
                      <input
                        type="submit"
                        value="Remove plant"
                        className="deleteBtn"
                      />
                    </form>
                    {isAdmin && (
                    <form className="editForm smallForm deleteBtnMargin" onSubmit={handleDestroySubmit}>
                      <input
                        type="submit"
                        value="Destroy plant"
                        className="deleteBtn"
                      />
                    </form>
                    )}
                  </div>
                  <div className="editFormDiv">

                  </div>
                </MDBModalTitle>
              </MDBModalHeader>
              <MDBModalBody>

                  <MaintenanceSelector type={"plant"} selected_plant_id={selected_plant_id} setSelected_plant_id={setSelected_plant_id} selectedUid={selectedUid} setSelectedUid={setSelectedUid} repeatAction={repeatAction}/>
                  {/* <b style={{marginTop:"20px"}}>Latest maintence on this plant:</b> */}
              </MDBModalBody>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      )}

      {/* Plot Maintenance Modal - for all plants in the plot */}
      {plotMaintenanceModal && (
        <MDBModal show={plotMaintenanceModal} setShow={setPlotMaintenanceModal} tabIndex="-1">
          <MDBModalDialog scrollable>
            <MDBModalContent
              style={{ maxHeight: "calc(100vh - 4rem)", overflowY: "auto", WebkitOverflowScrolling: "touch" }}
            >
              <MDBModalHeader>
                <MDBModalTitle>
                  <div className="">
                    <button
                      className="close-btn"
                      onClick={togglePlotMaintenanceModal}
                    >
                      <FaTimes />
                    </button>
                    <h1 className="title">{selectedUid}</h1>
                    <p>Applying maintenance to all plants in this plot ({allPlantIds.length} plants)</p>
                  </div>
                </MDBModalTitle>
              </MDBModalHeader>
              <MDBModalBody>
                <div>
                  <MaintenanceSelector 
                    type={"plot"} 
                    selected_plant_id={selected_plant_id} 
                    setSelected_plant_id={setSelected_plant_id} 
                    selectedUid={selectedUid} 
                    setSelectedUid={setSelectedUid} 
                    repeatAction={(repeat) => {
                      if (!repeat) {
                        setPlotMaintenanceModal(false);
                      }
                    }} 
                  />
                </div>
              </MDBModalBody>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      )}

      {/* Selected Plants Maintenance Modal */}
      {selectedPlantsMaintenanceModal && (
        <MDBModal show={selectedPlantsMaintenanceModal} setShow={setSelectedPlantsMaintenanceModal} tabIndex="-1">
          <MDBModalDialog scrollable>
            <MDBModalContent
              style={{ maxHeight: "calc(100vh - 4rem)", overflowY: "auto", WebkitOverflowScrolling: "touch" }}
            >
              <MDBModalHeader>
                <MDBModalTitle>
                  <div className="">
                    <button
                      className="close-btn"
                      onClick={toggleSelectedPlantsMaintenanceModal}
                    >
                      <FaTimes />
                    </button>
                    <h1 className="title">{selectedUid}</h1>
                    <p>Applying maintenance to selected plants ({selected_plant_ids.size} plants)</p>
                  </div>
                </MDBModalTitle>
              </MDBModalHeader>
              <MDBModalBody>
                <div>
                  <MaintenanceSelector 
                    type={"selected"} 
                    selected_plant_id={selected_plant_id} 
                    setSelected_plant_id={setSelected_plant_id} 
                    selectedUid={selectedUid} 
                    setSelectedUid={setSelectedUid} 
                    repeatAction={(repeat) => {
                      if (!repeat) {
                        setSelectedPlantsMaintenanceModal(false);
                      }
                    }} 
                  />
                </div>
              </MDBModalBody>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      )}

    </div>
  );
}


export default requireAuth(Plot);
